/*
Copyright (c) Uber Technologies, Inc.

This source code is licensed under the MIT license found in the
LICENSE file in the root directory of this source tree.
*/
import * as React from 'react';
import { useMemo, useSyncExternalStore } from 'react';
import { Base2FeaturesContext } from './base2-features-context';
import type { Base2FeaturesConfig } from './types';
export type Base2FeaturesProviderProps = {
  children: React.ReactNode;
  /**
   * Base 2.0 feature configuration to provide. Accepts the same
   * `'all' | { colors?, elevation?, material?, cornerRadius?, typography?, cssTheming? } | undefined`
   * shape as the underlying context. The value's identity is stabilized so callers
   * may pass a fresh object literal on every render without re-rendering every
   * downstream consumer of `Base2FeaturesContext`.
   *
   * NOTE: DO NOT ENABLE BASE 2.0 FEATURES if you have not spoken to the Base
   * Web team. We are doing a very intentional rollout, and there can be many
   * changes to this in upcoming releases.
   */
  value: Base2FeaturesConfig | undefined;
};

/**
 * The `data-base-version-*` attributes this provider watches on the document
 * root (`<html>`). Each entry maps a feature key to its DOM attribute name.
 */
const BASE_VERSION_ATTRIBUTES = ({
  colors: 'data-base-version-color',
  elevation: 'data-base-version-elevation',
  material: 'data-base-version-material',
  typography: 'data-base-version-typography'
} as const);

/** The attribute names as a flat array — used for the MutationObserver filter. */
const BASE_VERSION_ATTRIBUTE_NAMES = Object.values(BASE_VERSION_ATTRIBUTES);

/** Override state for each watched feature — `undefined` means "no override". */
type FeatureOverrides = Readonly<{
  colors: boolean | undefined;
  elevation: boolean | undefined;
  material: boolean | undefined;
  typography: boolean | undefined;
}>;
const NO_OVERRIDES: FeatureOverrides = Object.freeze({
  colors: undefined,
  elevation: undefined,
  material: undefined,
  typography: undefined
});

/**
 * Reads `data-base-version-*` attributes off the document root and maps each
 * to a feature override:
 *   "2"                  → `true`      (opt into Base 2.0)
 *   "1"                  → `false`     (explicit opt-out — Base 1.0)
 *   absent / other value → `undefined` (no override — defer to the `value` prop)
 */
const readOverridesFromDocumentRoot = (): FeatureOverrides => {
  if (typeof document === 'undefined') return NO_OVERRIDES;
  const read = (attr: string): boolean | undefined => {
    const value = document.documentElement.getAttribute(attr);
    if (value === '2') return true;
    if (value === '1') return false;
    return undefined;
  };
  return {
    colors: read(BASE_VERSION_ATTRIBUTES.colors),
    elevation: read(BASE_VERSION_ATTRIBUTES.elevation),
    material: read(BASE_VERSION_ATTRIBUTES.material),
    typography: read(BASE_VERSION_ATTRIBUTES.typography)
  };
};
const isSameOverrides = (a: FeatureOverrides, b: FeatureOverrides): boolean => a.colors === b.colors && a.elevation === b.elevation && a.material === b.material && a.typography === b.typography;

/** Returns `true` when any override is actively set (not all `undefined`). */
const hasAnyOverride = (o: FeatureOverrides): boolean => o.colors !== undefined || o.elevation !== undefined || o.material !== undefined || o.typography !== undefined;

/**
 * The overrides live on the document root, so they are the same for every
 * provider: one `MutationObserver` and one cached snapshot serve the whole
 * tree, however many providers are mounted. The snapshot reference only changes
 * when a watched attribute actually changes value, which is what keeps
 * `useSyncExternalStore` (and every memo downstream of it) from re-rendering.
 */
let snapshot: FeatureOverrides = NO_OVERRIDES;
let observer: MutationObserver | undefined;
const listeners = new Set<() => void>();

/** Re-reads the DOM into `snapshot`. Returns whether the value changed. */
const refreshSnapshot = (): boolean => {
  const next = readOverridesFromDocumentRoot();
  if (isSameOverrides(snapshot, next)) return false;
  snapshot = next;
  return true;
};
const handleMutation = () => {
  if (refreshSnapshot()) {
    for (const listener of listeners) listener();
  }
};
const subscribe = (listener: () => void): (() => void) => {
  listeners.add(listener);
  const canObserve = typeof document !== 'undefined' && typeof MutationObserver !== 'undefined';
  if (observer === undefined && canObserve) {
    observer = new MutationObserver(handleMutation);
    observer.observe(document.documentElement, {
      attributes: true,
      attributeFilter: [...BASE_VERSION_ATTRIBUTE_NAMES]
    });
  }
  return () => {
    listeners.delete(listener);
    if (listeners.size === 0) {
      observer?.disconnect();
      observer = undefined;
    }
  };
};
const getSnapshot = (): FeatureOverrides => {
  // With no observer attached the cache can be stale — an attribute may have
  // been set before the first mount, or between the last unmount and now — so
  // read through to the DOM. `refreshSnapshot` keeps the reference identical
  // when the values match, which `useSyncExternalStore` requires.
  if (observer === undefined) refreshSnapshot();
  return snapshot;
};
const getServerSnapshot = (): FeatureOverrides => NO_OVERRIDES;

/**
 * Memoized wrapper around `Base2FeaturesContext.Provider`. Use this instead of
 * the raw context provider so downstream consumers are not invalidated when
 * the caller passes a freshly constructed object literal each render.
 *
 * Also watches `data-base-version-color`, `data-base-version-elevation`,
 * `data-base-version-material`, and `data-base-version-typography` on the
 * document root via a `MutationObserver`, so tooling that flips these
 * attributes directly on `<html>` (browser extensions, devtools, debugging
 * scripts) can force the active version without going through the `value`
 * prop. When present, the DOM attribute takes precedence over the
 * corresponding `value.*` field for as long as it stays set to `"1"` or
 * `"2"`; any other value (including removal) falls back to `value.*`.
 *
 * This is the only place the two sources are merged, so everything below reads
 * the resolved features with the plain `useBase2Features()` hook. `BaseProvider`
 * renders one of these at the top of its subtree, which is what makes the
 * attributes work in apps that never register the fusion plugin.
 */
export const Base2FeaturesProvider = ({
  children,
  value
}: Base2FeaturesProviderProps) => {
  const overrides = useSyncExternalStore(subscribe, getSnapshot, getServerSnapshot);
  const isAll = value === 'all';
  const isUndefined = value === undefined;
  // Decompose to primitives so `useMemo` deps are stable even when the caller
  // passes a fresh object literal each render. Only an explicit `true`
  // activates each flag; everything else is inherit / no-op.
  const colorsFromValue = isAll ? true : !isUndefined && value.colors === true;
  const colors = overrides.colors ?? colorsFromValue;
  const elevationFromValue = isAll ? true : !isUndefined && value.elevation === true;
  const elevation = overrides.elevation ?? elevationFromValue;
  const materialFromValue = isAll ? true : !isUndefined && value.material === true;
  const material = overrides.material ?? materialFromValue;
  const cornerRadius = isAll ? true : !isUndefined && value.cornerRadius === true;
  const typographyFromValue = isAll ? true : !isUndefined && value.typography === true;
  const typography = overrides.typography ?? typographyFromValue;
  // cssTheming is never set by 'all' — it's always an explicit opt-in.
  const cssTheming = !isAll && !isUndefined && value.cssTheming === true;
  const anyOverride = hasAnyOverride(overrides);
  const stable = useMemo<Base2FeaturesConfig | undefined>(() => {
    // Only collapse to the `value` prop's own shorthand when the DOM isn't
    // overriding any feature — otherwise the override would be lost, since
    // `undefined`/`'all'` are resolved from scratch by the context.
    if (!anyOverride) {
      if (isUndefined) return undefined;
      if (isAll) return 'all';
    }
    return {
      colors,
      elevation,
      material,
      cornerRadius,
      typography,
      cssTheming
    };
  }, [anyOverride, isUndefined, isAll, colors, elevation, material, cornerRadius, typography, cssTheming]);
  return <Base2FeaturesContext.Provider value={stable}>{children}</Base2FeaturesContext.Provider>;
};